import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { DEFAULT_DEMO_KM } from "./journeyModel";

export const PUBLIC_AGGREGATE_COLLECTION = "publicRaceAggregates";
export const BIG_CANADA_RUN_AGGREGATE_ID = "big-canada-run-2026";
export const AGGREGATE_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const DEMO_PARTICIPANT_COUNT = 4217;
const DEMO_LAST_UPDATED_AT = "2026-07-02T15:50:00-04:00";

function toNonNegativeNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

function normalizeTimestamp(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  return null;
}

export function useBigCanadaRunProgressData({ debugEnabled = false } = {}) {
  const [debugDistanceKm, setDebugDistanceKm] = useState(DEFAULT_DEMO_KM);
  const lastUpdatedAtRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [publicProgressData, setPublicProgressData] = useState({
    distanceKm: 0,
    participantCount: 0,
    lastUpdatedAt: null,
    loading: true,
    error: null,
    source: "public-aggregate",
  });

  useEffect(() => {
    if (debugEnabled) return undefined;

    let isMounted = true;
    let refreshTimeoutId = null;
    const aggregateRef = doc(db, PUBLIC_AGGREGATE_COLLECTION, BIG_CANADA_RUN_AGGREGATE_ID);

    const clearRefreshTimeout = () => {
      if (!refreshTimeoutId) return;
      window.clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    };

    const getNextRefreshDelay = (lastUpdatedAt) => {
      const lastUpdatedMs = lastUpdatedAt ? new Date(lastUpdatedAt).getTime() : NaN;

      if (!Number.isFinite(lastUpdatedMs)) {
        return AGGREGATE_REFRESH_INTERVAL_MS;
      }

      return Math.max(60000, lastUpdatedMs + AGGREGATE_REFRESH_INTERVAL_MS - Date.now());
    };

    const scheduleNextRefresh = (lastUpdatedAt) => {
      clearRefreshTimeout();
      if (document.visibilityState !== "visible") return;

      refreshTimeoutId = window.setTimeout(() => {
        fetchPublicProgressData();
      }, getNextRefreshDelay(lastUpdatedAt));
    };

    const fetchPublicProgressData = async ({ showLoading = false } = {}) => {
      if (showLoading) {
        setPublicProgressData((currentData) => ({
          ...currentData,
          loading: true,
          error: null,
        }));
      }

      try {
        const snapshot = await getDoc(aggregateRef);
        const data = snapshot.data() || {};
        const lastUpdatedAt = normalizeTimestamp(data.lastUpdatedAt);

        if (!isMounted) return;

        lastUpdatedAtRef.current = lastUpdatedAt;
        hasLoadedRef.current = true;
        setPublicProgressData({
          distanceKm: toNonNegativeNumber(data.distanceKm),
          participantCount: Math.round(toNonNegativeNumber(data.participantCount)),
          lastUpdatedAt,
          loading: false,
          error: snapshot.exists() ? null : "Aggregate data is not available yet.",
          source: "public-aggregate",
        });

        scheduleNextRefresh(lastUpdatedAt);
      } catch (error) {
        if (!isMounted) return;

        setPublicProgressData((currentData) => ({
          ...currentData,
          loading: false,
          error: error.message || "Unable to load aggregate data.",
        }));

        scheduleNextRefresh(lastUpdatedAtRef.current);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearRefreshTimeout();
        return;
      }

      if (!hasLoadedRef.current) {
        fetchPublicProgressData({ showLoading: true });
        return;
      }

      const lastUpdatedAt = lastUpdatedAtRef.current;
      const lastUpdatedMs = lastUpdatedAt ? new Date(lastUpdatedAt).getTime() : NaN;
      const shouldRefresh =
        !Number.isFinite(lastUpdatedMs) ||
        Date.now() >= lastUpdatedMs + AGGREGATE_REFRESH_INTERVAL_MS;

      if (shouldRefresh) {
        fetchPublicProgressData();
        return;
      }

      scheduleNextRefresh(lastUpdatedAt);
    };

    fetchPublicProgressData({ showLoading: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      clearRefreshTimeout();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [debugEnabled]);

  const debugProgressData = useMemo(
    () => ({
      distanceKm: debugDistanceKm,
      participantCount: DEMO_PARTICIPANT_COUNT,
      lastUpdatedAt: DEMO_LAST_UPDATED_AT,
      loading: false,
      error: null,
      source: "demo",
    }),
    [debugDistanceKm]
  );

  return {
    progressData: debugEnabled ? debugProgressData : publicProgressData,
    setDistanceKm: setDebugDistanceKm,
  };
}
